import React, {useEffect, useMemo, useState} from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import useFilters from "../../hooks/filters/useFilters";
import useDebouncedValue from "../../helpers/filters/useDebouncedValue";
import useProductData from "../../hooks/useProductData";
import useUserData from "../../hooks/user/useUserData";
import {PRODUCTS_LIST} from "../../locales/pages/namespaces";
import {normalizeProductsList} from "../../helpers/products/normalizeProductList";
import {normalizeProductFilters} from "../../helpers/products/normalizeProductFilters";
import FiltersSidebar from "../../components/general/filters/filtersSidebar";
import ProductsGrid from "./productListComponents/productsGrid";


export default function ProductsIndex() {
    const { t } = useTranslation(PRODUCTS_LIST);
    const location = useLocation();
    const { language } = useUserData();
    const searchParams = new URLSearchParams(location.search);
    const locationState = location.state || {};

    const normalizeIds = (value) => {
        if (!value) return [];

        if (Array.isArray(value)) {
            return value.map(String).filter(Boolean);
        }

        return String(value)
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
    };

    const categoryParamFromQuery = searchParams.get("category");
    const brandParamFromQuery = searchParams.get("brand");

    const categoryParam = normalizeIds(
        categoryParamFromQuery || locationState.categoryId
    );

    const brandParam = normalizeIds(
        brandParamFromQuery || locationState.brandId
    );
    const categoryParamKey = categoryParam.join(",");
    const brandParamKey = brandParam.join(",");
    const {
        filters,
        page,
        setPage,
        setFilters,
        toggleFilter,
        setRange,
        setSorting,
        resetAll,
    } = useFilters({
        initialCategories: categoryParam,
        initialBrands: brandParam
    });

    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            categories: categoryParam,
            brands: brandParam,
            products: [],
            colors: [],
            priceMin: null,
            priceMax: null,
            sorting: "",
        }));
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryParamKey, brandParamKey, setFilters, setPage]);

    const debouncedFilters = useDebouncedValue(filters, 250);
    const debouncedPage = useDebouncedValue(page, 250);
    const { products, loading, error } = useProductData({
        filters: debouncedFilters,
        sorting: filters.sorting,
        page: debouncedPage,
    });
    const normalizedProducts = products
        ? normalizeProductsList(products, language, debouncedPage, 12)
        : { items: [], totalPages: 1, page: 1, count: 0 };
    const normalizedFacets = products
        ? normalizeProductFilters(products, language)
        : {
            categories: [],
            products: [],
            brands: [],
            colors: [],
            price: { min: 0, max: 0 },
            count: 0,
        };
    const hasActiveFilters = useMemo(() => {
        const backendMin = normalizedFacets?.price?.min ?? 0;
        const backendMax = normalizedFacets?.price?.max ?? 0;

        const effectiveMin =
            typeof filters.priceMin === "number" ? filters.priceMin : backendMin;

        const effectiveMax =
            typeof filters.priceMax === "number" ? filters.priceMax : backendMax;

        const hasPriceFilter =
            effectiveMin !== backendMin || effectiveMax !== backendMax;

        return (
            filters.categories.length > 0 ||
            filters.products.length > 0 ||
            filters.brands.length > 0 ||
            filters.colors.length > 0 ||
            hasPriceFilter ||
            filters.sorting !== ""
        );
    }, [filters, normalizedFacets]);

    const handleSortChange = (value) => {
        setSorting(value);
    };

    if (loading && !products) {
        return (
            <Box
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 9999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.45)",
                    backdropFilter: "blur(10px)",
                }}
            >
                <Box
                    sx={{
                        width: 96,
                        height: 96,
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.82)",
                        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)",
                    }}
                >
                    <CircularProgress size={42} thickness={4} />
                </Box>
            </Box>
        );
    }
    if (error) {
        return (
            <Box sx={{  mx: "auto", width: "95%" }}>
                <Alert severity="error">{t("Failed to load filters")}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{  mx: "auto", width: "95%" }}>
            <Breadcrumbs separator="›" sx={{ mb: 1 }}>
                <Link
                    href="/"
                    underline="hover"
                    sx={{
                        fontFamily: "Roboto Condensed",
                        fontWeight: 400,
                        fontSize: 14,
                    }}
                >
                    BREW
                </Link>

                <Typography
                    color="textPrimary"
                    sx={{
                        fontFamily: "Roboto Condensed",
                        fontWeight: 400,
                        fontSize: 14,
                        textTransform: "uppercase",
                    }}
                >
                    {t("Products")}
                </Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    bgcolor: "#F0F0F0",
                    p: { xs: 3, md: 6 },
                    borderRadius: "30px",
                }}
            >
                <Stack sx={{ mb: 3 }} spacing={2}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            fontSize: { xs: 28, md: 35 },
                            color: "#111",
                            lineHeight: "90%",
                        }}
                    >
                        {t("ALL PRODUCTS")}
                    </Typography>

                </Stack>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={3}>
                        <Box
                            sx={{
                                position: { md: "sticky" },
                                top: { md: 120 },
                                alignSelf: "flex-start",
                            }}
                        >
                            <FiltersSidebar
                                data={normalizedFacets}
                                filteredProducts={normalizedProducts.items}
                                filters={filters}
                                onToggle={toggleFilter}
                                onRange={setRange}
                                showClear={hasActiveFilters}
                                onReset={() =>
                                    resetAll({
                                        min: normalizedFacets?.price?.min ?? 0,
                                        max: normalizedFacets?.price?.max ?? 0,
                                    })
                                }
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={9}>
                        <ProductsGrid
                            products={normalizedProducts.items}
                            loading={loading}
                            error={error}
                            totalPages={normalizedProducts.totalPages}
                            currentPage={normalizedProducts.page}
                            onPageChange={setPage}
                            sortValue={filters.sorting || "default"}
                            onSortChange={handleSortChange}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
